import * as React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useIntl } from '@leagueplatform/locales';
import {
  BasePageLayout,
  SideNav,
  ErrorBoundaryWrapper,
  ErrorPage,
} from '@web-config-app/core-react-ui';
import type { NavItemProps } from '@web-config-app/core-react-ui';
import {
  useConfigApp,
  useConfigAppParams,
  useConfigAppPath,
  useAuthoringEnvironments,
  useEntitySingleEnvironment,
  useHasPermissionsHelpers,
} from '@web-config-app/core-react';
import {
  checkModulePermissions,
  isMasonryContentEnabled,
  type ResolvedModule,
  type DomainEntityConfiguration,
  type EnvironmentKey,
} from '@web-config-app/core';
import { AppHeaderContainer } from '../app-header/app-header.container';
import { PermissionsGuard } from '../permissions-guard/permissions-guard.component';

/**
 * TabPage is the persistent layout for every tab. It renders the navigation
 * bar (top-level modules as tabs), a side nav built from the current tab's
 * child modules, and an <Outlet /> for child content (entity pages,
 * module pages, etc.).
 */
export const TabPage = () => {
  const { modules = [], entityConfigs, tenantId } = useConfigApp();
  const { tabId } = useConfigAppParams();
  const { formatMessage } = useIntl();
  const generateConfigPaths = useConfigAppPath();
  const { defaultAuthoringEnvironment } = useAuthoringEnvironments();
  const { getEntitySingleEnvironment } = useEntitySingleEnvironment();
  const defaultEnvironmentKey = defaultAuthoringEnvironment.environmentKey;
  const { pathname } = useLocation();
  const { hasAllPermissions } = useHasPermissionsHelpers({
    environmentKey: defaultAuthoringEnvironment.environmentKey,
    tenantId,
    isAuthenticated: true,
  });

  const currentTab = React.useMemo(
    () => modules.find((tabModule) => tabModule.id === tabId),
    [modules, tabId],
  );

  // Parse moduleId positionally from the known route structure:
  //   /tenants/:tenantId/:tabId/:moduleId/...
  // For tab-as-entity routes (/tenants/:tenantId/:tabId/environments/...)
  // there is no moduleId — the segment after tabId is 'environments', so
  // we treat that as undefined rather than a real module ID.
  const activeModuleId = React.useMemo(() => {
    const parts = pathname.split('/');
    const tenantsIdx = parts.indexOf('tenants');
    // parts: ['', 'tenants', tenantId, tabId, moduleId | 'environments', ...]
    const moduleIdSegment = tenantsIdx >= 0 ? parts[tenantsIdx + 3] : undefined;
    if (!moduleIdSegment || moduleIdSegment === 'environments') {
      return undefined;
    }
    return moduleIdSegment;
  }, [pathname]);

  const currentEnvironmentKey = React.useMemo(() => {
    const parts = pathname.split('/');
    const envIdx = parts.indexOf('environments');
    return envIdx >= 0
      ? (parts[envIdx + 1] as EnvironmentKey | undefined)
      : undefined;
  }, [pathname]);

  const sideNavItems: NavItemProps[] = React.useMemo(() => {
    const getEntityEnvironmentKey = (resolvedEntityId: string): string => {
      const entityConfig: DomainEntityConfiguration | undefined =
        entityConfigs[resolvedEntityId];
      const { singleEnv } = entityConfig
        ? getEntitySingleEnvironment({ domainEntity: entityConfig })
        : { singleEnv: undefined };
      // Preserve the user's currently-active environment when navigating
      // between multi-env entities in the side nav. Single-env
      // entities still honor their pinned environment. Falls back to the
      // default when the URL has no env segment (e.g. landing pages).
      return (
        singleEnv?.environmentKey ??
        currentEnvironmentKey ??
        defaultEnvironmentKey
      );
    };

    /**
     * Returns true when the module should appear in the side nav.
     * Entity modules are hidden when they've been removed by permission
     * filtering (i.e. no longer in entityConfigs). All module types are
     * hidden when the user lacks the module's required permissions.
     */
    const isModuleAccessible = (mod: ResolvedModule): boolean => {
      if (!checkModulePermissions(mod.permissions, hasAllPermissions)) {
        return false;
      }
      if (mod.id === 'masonry-generic-content' && !isMasonryContentEnabled()) {
        return false;
      }
      return mod.type !== 'entity' || mod.id in entityConfigs;
    };

    /**
     * Builds the nav path for a module. For entities directly under
     * the tab, moduleId is the entity. For entities inside a container,
     * moduleId is the container and childModuleId is the entity.
     * For containers, delegates to the first accessible child so YAML
     * order is honoured (a leading component page isn't skipped over).
     */
    const buildModulePath = (
      resolvedModule: ResolvedModule,
      parentContainerId?: string,
    ): string => {
      if (resolvedModule.type === 'entity') {
        const environmentKey = getEntityEnvironmentKey(resolvedModule.id);
        const { entityListPath } = generateConfigPaths({
          tabId,
          moduleId: parentContainerId ?? resolvedModule.id,
          childModuleId: parentContainerId ? resolvedModule.id : undefined,
          environmentKey,
        });
        return entityListPath;
      }

      if (resolvedModule.type === 'container') {
        const firstChild = (resolvedModule.modules ?? []).find(
          isModuleAccessible,
        );
        if (firstChild) {
          return buildModulePath(firstChild, resolvedModule.id);
        }
      }

      // Component/schema pages link to their own module path.
      // When inside a container, the path needs the container segment too
      // (e.g. /admin/tools/feature-flags, not /admin/feature-flags).
      const { modulePath } = generateConfigPaths({
        tabId,
        moduleId: parentContainerId ?? resolvedModule.id,
      });
      return parentContainerId
        ? `${modulePath}/${resolvedModule.id}`
        : modulePath;
    };

    const children = (currentTab?.modules ?? []).filter(isModuleAccessible);
    return children
      .filter((childModule) => {
        // Hide containers whose accessible children have all been filtered out
        if (childModule.type === 'container') {
          return (childModule.modules ?? []).some(isModuleAccessible);
        }
        return true;
      })
      .map((childModule) => {
        const childModules = (childModule.modules ?? []).filter(
          isModuleAccessible,
        );
        // A module gets sub-nav items when it's a container or a
        // componentPage acting as a landing page with children.
        const hasNavChildren =
          (childModule.type === 'container' ||
            childModule.type === 'componentPage') &&
          childModules.length > 0;

        // Landing pages render at the module's own path (not a child route),
        // so the nav link uses `end` matching. This applies to componentPages
        // — including componentPages that have child modules (landing pages
        // with sub-navigation).
        const hasLandingPage = childModule.type === 'componentPage';

        const navSubItems = hasNavChildren
          ? childModules.map((grandchild) => ({
              name: formatMessage({ id: grandchild.labelKey }, { count: 2 }),
              path: buildModulePath(grandchild, childModule.id),
            }))
          : [];

        // A module is active if it matches the URL directly, or if any
        // of its children match (so container children are revealed).
        const isActive =
          activeModuleId === childModule.id ||
          childModules.some((grandchild) => grandchild.id === activeModuleId);

        return {
          name: formatMessage({ id: childModule.labelKey }, { count: 2 }),
          path: buildModulePath(childModule),
          icon: childModule.icon,
          isActive,
          hasLandingPage,
          navItems: navSubItems,
        };
      });
  }, [
    currentTab,
    tabId,
    formatMessage,
    generateConfigPaths,
    defaultEnvironmentKey,
    currentEnvironmentKey,
    activeModuleId,
    entityConfigs,
    getEntitySingleEnvironment,
    hasAllPermissions,
  ]);

  if (!currentTab) return <ErrorPage />;

  return (
    <PermissionsGuard permissions={currentTab.permissions}>
      <BasePageLayout
        topBar={
          <AppHeaderContainer currentEnvironment={currentEnvironmentKey} />
        }
        sidePanel={
          sideNavItems.length > 0 ? <SideNav items={sideNavItems} /> : undefined
        }
      >
        <ErrorBoundaryWrapper>
          <Outlet />
        </ErrorBoundaryWrapper>
      </BasePageLayout>
    </PermissionsGuard>
  );
};
